import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const AcceptInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [organizationName, setOrganizationName] = useState<string | null>(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    if (!isAuthenticated) {
      // First validate the token before redirecting
      validateToken();
      return;
    }

    acceptInvitation();
  }, [token, isAuthenticated, navigate]);

  const validateToken = async () => {
    if (!token) return;

    try {
      setLoading(true);
      
      const { data, error } = await supabase.functions.invoke(
        'validate-invitation-token',
        { body: { token } }
      );

      if (error) {
        throw new Error('Failed to validate invitation');
      }

      if (!data.valid) {
        setError(data.error || 'Invalid invitation');
        setLoading(false);
        return;
      }

      setOrganizationName(data.organizationName);
      // Redirect to auth page with invitation token
      navigate(`/auth?invitation=${token}`);
      
    } catch (error: any) {
      console.error('Error validating token:', error);
      setError('Failed to validate invitation');
      setLoading(false);
    }
  };

  const acceptInvitation = async () => {
    if (!token || !user) return;

    try {
      setLoading(true);
      
      // Validate token first using secure edge function
      const { data: validation, error: validationError } = await supabase.functions.invoke(
        'validate-invitation-token',
        { body: { token } }
      );

      if (validationError) {
        throw new Error('Failed to validate invitation');
      }

      if (!validation.valid) {
        throw new Error(validation.error || 'Invalid invitation');
      }

      if (validation.email !== user.email) {
        throw new Error('This invitation is for a different email address');
      }

      // Accept the invitation using the secure database function
      const { data: result, error: acceptError } = await supabase.rpc(
        'accept_organization_invitation',
        { invitation_token: token }
      );

      if (acceptError) {
        throw new Error(acceptError.message || 'Failed to accept invitation');
      }

      if (result && typeof result === 'object' && 'error' in result) {
        throw new Error((result as any).error);
      }

      setOrganizationName(validation.organizationName);
      setSuccess(true);
      
      toast({
        title: "Invitation Accepted",
        description: `You have successfully joined ${validation.organizationName}!`,
      });

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/admin/dashboard');
      }, 2000);

    } catch (error: any) {
      console.error('Error accepting invitation:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin mb-4" />
            <p>Processing invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Organization Invitation</CardTitle>
          <CardDescription>
            {success ? 'Welcome to your new organization!' : 'Accept your invitation to join'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {success ? (
            <div className="text-center space-y-4">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  You have successfully joined {organizationName}! Redirecting to dashboard...
                </AlertDescription>
              </Alert>
            </div>
          ) : error ? (
            <div className="text-center space-y-4">
              <XCircle className="h-16 w-16 text-red-500 mx-auto" />
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
              <div className="flex gap-2 justify-center">
                <Button onClick={() => navigate('/auth')} variant="outline">
                  Go to Login
                </Button>
                <Button onClick={() => navigate('/')}>
                  Go Home
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInvitation;